import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import {
    ADDRESSBYGOOGLE_COLUMN_URI,
    AUTOCOMPLETEFROMENUMERATION_COLUMN_URI,
    AUTOCOMPLETEFROMURI_COLUMN_URI,
    AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI,
    CHECKBOX_COLUMN_URI,
    CODEBARSCANNERWITHSELECT_URI,
    CODEBAR_SCANNER_URI,
    DATEPICKER_COLUMN_URI,
    DeleteDTypeData,
    DType,
    DTypeCol,
    DTypeRepository,
    DTypeStatus,
    INTEGER_COLUMN_URI,
    LANGUAGETEXT_COLUMN_URI,
    NextLocator, Pagination, RADIO_COLUMN_URI,
    RATING_COLUMN_URI,
    REAL_COLUMN_URI,
    SearchDTypeDataCollection,
    SELECTFACADE_COLUMN_URI,
    SELECTFROMCONCEPTSERVICE_COLUMN_URI,
    SELECTFROMENUMERATION_COLUMN_URI,
    SELECTFROMMASTERDATA_COLUMN_URI,
    SELECTFROMURI_COLUMN_URI,
    SELECTMASTER_COLUMN_URI,
    SELECTNEXTTASKTYPE_COLUMN_URI,
    SELECTSERVICEUSERASSIGNMENT_COLUMN_URI,
    SELECTSERVICE_COLUMN_URI,
    SELECTWITHGIVENOPTIONS_COLUMN_URI,
    TEXT_COLUMN_URI,
    TIMEPICKER_COLUMN_URI,
    TIMERANGESCONVERTER_COLUMN_URI,
    TRISTATE_COLUMN_URI,
    SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI,
    JsonReply,
    AUTOCOMPLETE_FROM_MASTER_DATA,
    SELECTFROMRESOURCESCANNER_URI
} from '@grupo-constant/next-dtype/types';
import { compareAsc, compareDesc, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, catchError, map, Observable, Subject, throwError } from 'rxjs';
import { DTypeService } from '../dtype/dtype.service';
import { RetrieveDTypeDataCollectionBody } from '../dtype/dtype.types';
import { NextMethodsEnum } from '../enums/next-methods.enum';
import { HttpService } from '../http/http.service';
import { LocalizePipe } from '../pipes/localize.pipe';
import { LocaleString } from '../translate/localeString.model';
import { WebSocketMessage } from '../websocket/websocket.types';
import { DownloadMasterXSSFBody, FlushMasterCategoryTreeReply, MasterData, NextValueBody, NextValueReply, RetrieveLocationChangeRequest, RetrieveLocationChangeRequestReply, RetrieveMasterCategoryTreeReply, RetrieveMasterDataAsSelectOptionReply, RetrieveModifiedMasterInstaceBody, RetrieveNewMasterInstaceBody, RetrieveNewMasterInstanceReply, UploadMasterXSSFBody, UploadMasterXSSFResponse } from './master-data.types';
import { CategoryNode } from '../../dialogs/category-tree-dialog/category-tree-dialog.component';

export const MASTER_DATA_URI = 'dtype://com.cloudcodice.feature.master.Master';

@Injectable({
    providedIn: 'root'
})
export class MasterDataService {

    readonly selectTypes = [SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI, SELECTFROMRESOURCESCANNER_URI, SELECTFROMMASTERDATA_COLUMN_URI,
        SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI,
        CODEBARSCANNERWITHSELECT_URI, SELECTSERVICE_COLUMN_URI, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI];

    readonly autocompleteTypes = [AUTOCOMPLETEFROMENUMERATION_COLUMN_URI, AUTOCOMPLETEFROMURI_COLUMN_URI, AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA];

    private masterUri: BehaviorSubject<string> = new BehaviorSubject(undefined);
    private masterCaption: BehaviorSubject<LocaleString> = new BehaviorSubject(undefined);
    private _webSocketMessage: Subject<WebSocketMessage> = new Subject();

    constructor(
        private dtypeService: DTypeService,
        private httpService: HttpService,
        private localize: LocalizePipe,
    ) { }

    get masterUri$(): Observable<string> {
        return this.masterUri.asObservable();
    }

    get masterCaption$(): Observable<LocaleString> {
        return this.masterCaption.asObservable();
    }

    /**
     * Getter for webSocket
     */
    get webSocketMessage$(): Observable<WebSocketMessage> {
        return this._webSocketMessage.asObservable();
    }

    setMasterUri(uri: string) {
        this.masterUri.next(uri);
    }

    setMasterCaption(caption: LocaleString) {
        this.masterCaption.next(caption);
    }

    setWebSocketMessage(message: WebSocketMessage) {
        this._webSocketMessage.next(message);
    }

    retrieveMasterDataDType() {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: MASTER_DATA_URI, status: DTypeStatus.DESIGN, bindableOptions: true }, includeEmbedded: true });
    }

    retrieveMasterDataDataCollection(body: RetrieveDTypeDataCollectionBody) {
        return this.dtypeService.retrieveDTypeDataCollection(body);
    }

    retrieveMasterDataDTypeByUri(uri: string, status: DTypeStatus, locator?: NextLocator): Promise<MasterData> {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri, status, locator, bindableOptions: true }, includeEmbedded: true });
    }

    retrieveMasterDataDTypeRepositoryByUri(uri: string, status: DTypeStatus, locator?: NextLocator): Observable<DTypeRepository> {
        return this.dtypeService.retrieveDTypeRepositoryByUri({ uri, status, locator, bindableOptions: true });
    }

    retrieveMasterDataDtypeForm(values: any, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormByUri({ uri: MASTER_DATA_URI, status: DTypeStatus.DESIGN, bindableOptions: true }, values, options);
    }

    retrieveMasterDataDtypeFormByUri(uri: string, values: any, status: DTypeStatus, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormByUri({ uri, status, bindableOptions: true }, values, options);
    }

    retrieveMasterDataDtypeFormFromDtype(dtype: DType, values?: any, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormFromDtype(dtype, values, options);
    }

    retrieveMasterDataAsSelectOptions(uri: string) {
        return this.httpService.post(NextMethodsEnum.retrieveMasterDataAsSelectOption, { uri }).pipe(
            catchError((error) => {
                console.error(error);
                return throwError(() => error.error);
            }),
            map((reply: RetrieveMasterDataAsSelectOptionReply) => reply.selectOptions ?? [])
        );
    }

    flushMasterData(masterData: MasterData, locator: NextLocator) {
        return this.dtypeService.flushDType({ locator, facadeUri: MASTER_DATA_URI, dtype: masterData });
    }

    updateLive(uri: string) {
        return this.dtypeService.forwardDTypeToLive({uri});
    }

    updateMasterData(repo: DTypeRepository, goLive?: boolean) {
        this.dtypeService.updateDType(repo.uri, repo, goLive);
    }

    emitUpdateSidebarEvent() {
        this.dtypeService.emitUpdateSidebarEvent();
    }

    updateColumns(columns: DTypeCol[]) {
        this.dtypeService.columns$.next(columns);
    }

    deleteData(body: DeleteDTypeData) {
        return this.dtypeService.deleteDTypeData(body);
    }

    searchData(body: SearchDTypeDataCollection) {
        return this.dtypeService.searchDTypeDataCollection(body);
    }

    retrieveNewMasterDataInstance(body: RetrieveNewMasterInstaceBody) {
        return this.httpService.post<RetrieveNewMasterInstanceReply>(NextMethodsEnum.retrieveNewMasterInstance, body);
    }

    retrieveModifiedMasterDataInstance(body: RetrieveModifiedMasterInstaceBody) {
        return this.httpService.post<RetrieveNewMasterInstanceReply>(NextMethodsEnum.retrieveModifiedMasterInstance, body);
    }

    getLocatorFromString(stringLocator: string): NextLocator {
        return this.dtypeService.getLocatorFromString(stringLocator);
    }

    downloadMasterXSSF(body: DownloadMasterXSSFBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadNextMasterXSSF, body, 'blob');
    }

    downloadStructureXSSF(body: DownloadMasterXSSFBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadNextMasterFormStructureXSSF, body, 'blob');
    }

    uploadExcel(body: UploadMasterXSSFBody): Observable<UploadMasterXSSFResponse> {
        return this.httpService.post<UploadMasterXSSFResponse>(NextMethodsEnum.flushDTypeExcelUpload, body);
    }

    retrieveMasterCategoryTree(uri: string) {
        return this.httpService.post<RetrieveMasterCategoryTreeReply>(NextMethodsEnum.retrieveMasterCategoryTree, { uri });
    }

    flushMasterCategoryTree(uri: string, tree: CategoryNode[]) {
        return this.httpService.post<FlushMasterCategoryTreeReply>(NextMethodsEnum.flushMasterCategoryTree, { uri, tree });
    }

    retrieveLocationChangeRequest(request: RetrieveLocationChangeRequest) {
        return this.httpService.post<RetrieveLocationChangeRequestReply>(NextMethodsEnum.retrieveLocationChangeRequest, request);
    }

    flushLocationChangeResult(oid: string, approve: boolean) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.flushLocationChangeResult, { oid, approve });
    }

    notifySystemChangesForUsers() {
        return this.httpService.post<JsonReply>(NextMethodsEnum.notifySystemChangesForUsers, {});
    }

    nextValue(body: NextValueBody) {
        return this.httpService.post<NextValueReply>(NextMethodsEnum.nextValue, body);
    }

    getViewItems(items: any[], totalLength: number, page: number = 0, size: number = 10) {
        // Clone the list to prevent accidental updates
        let itemList = cloneDeep(items);

        // Paginate - Start

        // Calculate pagination details
        const begin = page * size;
        const end = Math.min((size * (page + 1)), totalLength);
        const lastPage = Math.max(Math.ceil(totalLength / size), 1);

        // Prepare the pagination object
        let pagination: Partial<Pagination>;

        // If the requested page number is bigger than
        // the last possible page number, return null for
        // tasks but also send the last possible page so
        // the app can navigate to there
        if (page > lastPage) {
            pagination = {
                lastPage
            };
        } else {
            // Paginate the results
            itemList = itemList.slice(begin, end);

            // Prepare the pagination
            pagination = {
                length: totalLength,
                size,
                page,
                lastPage: lastPage,
                startIndex: begin,
                endIndex: end - 1
            };
        }

        return { list: itemList, pagination };
    }

    sortItems(items: any[], sortColumn: DTypeCol, sortDir: SortDirection) {
        return items.sort((a, b) => {
            if (!sortColumn) {
                return 0;
            }
            if (sortColumn.type === TEXT_COLUMN_URI || sortColumn.type === CODEBAR_SCANNER_URI || this.selectTypes.includes(sortColumn.type)
                || this.autocompleteTypes.includes(sortColumn.type)) { // Tipo string generico
                return this.compareStrings(a[sortColumn.name] ?? '', b[sortColumn.name] ?? '', sortDir);
            } else if (sortColumn.type === ADDRESSBYGOOGLE_COLUMN_URI) {
                return this.compareStrings(a.address.name ?? '', b.address.name ?? '', sortDir);
            } else if (sortColumn.type === LANGUAGETEXT_COLUMN_URI) {
                const nameA = typeof a[sortColumn.name] === 'string' ? a[sortColumn.name] : this.localize.transform(a[sortColumn.name]);
                const nameB = typeof b[sortColumn.name] === 'string' ? b[sortColumn.name] : this.localize.transform(b[sortColumn.name]);
                return this.compareStrings(nameA ?? '', nameB ?? '', sortDir);
            } else if (sortColumn.type === TIMERANGESCONVERTER_COLUMN_URI) {
                return this.compareNumbers(parseInt(a[sortColumn.name], 10), parseInt(b[sortColumn.name], 10), sortDir);
            } else if (sortColumn.type === INTEGER_COLUMN_URI || sortColumn.type === REAL_COLUMN_URI || sortColumn.type === RATING_COLUMN_URI) {
                return this.compareNumbers(a[sortColumn.name], b[sortColumn.name], sortDir);
            } else if (sortColumn.type === CHECKBOX_COLUMN_URI || sortColumn.type === RADIO_COLUMN_URI) {
                return this.compareBooleans(a[sortColumn.name], b[sortColumn.name], sortDir);
            } else if (sortColumn.type === TRISTATE_COLUMN_URI) {
                return this.compareTristates(a[sortColumn.name], b[sortColumn.name], sortDir);
            } else if (sortColumn.type === DATEPICKER_COLUMN_URI) {
                return this.compareDates(a[sortColumn.name], b[sortColumn.name], sortDir);
            } else if (sortColumn.type === TIMEPICKER_COLUMN_URI) {
                return this.compareTimes(a[sortColumn.name], b[sortColumn.name], sortDir);
            }
        });
    }

    private compareStrings(string1: string, string2: string, dir: SortDirection) {
        if (string1 === '') {
            if (string2 === '') {
                return 0;
            } else {
                return dir === 'asc' ? 0 : -1;
            }
        } else if (string2 === '') {
            if (string1 === '') {
                return 0;
            } else {
                return dir === 'asc' ? -1 : 0;
            }
        } else {
            return dir === 'asc' ? string1.localeCompare(string2) : string2.localeCompare(string1);
        }
    }

    private compareNumbers(number1: number, number2: number, dir: SortDirection) {
        if (number1 === number2) {
            return 0;
        } else if (number1 < number2) {
            return dir === 'asc' ? -1 : 1;
        } else {
            return dir === 'asc' ? 1 : -1;
        }
    }

    private compareDates(date1: string, date2: string, dir: SortDirection) {
        const d1 = parseISO(date1);
        const d2 = parseISO(date2);
        if (dir === 'asc') {
            return compareAsc(d1, d2);
        } else {
            return compareDesc(d1, d2);
        }
    }

    private compareTimes(time1: string, time2: string, dir: SortDirection) {
        const t1 = time1.split(':', 3);
        const t2 = time2.split(':', 3);
        const compare0 = this.compareNumbers(parseInt(t1[0], 10), parseInt(t2[0], 10), dir);
        if (compare0 !== 0) {
            return compare0;
        } else if (this.compareNumbers(parseInt(t1[1], 10), parseInt(t2[1], 10), dir) !== 0) {
            return this.compareNumbers(parseInt(t1[1], 10), parseInt(t2[1], 10), dir);
        } else {
            return this.compareNumbers(parseInt(t1[2], 10), parseInt(t2[2], 10), dir);
        }
    }

    private compareBooleans(check1: boolean, check2: boolean, dir: SortDirection) {
        if (check1 === check2) {
            return 0;
        } else if (check1 === true && check2 === false) {
            return dir === 'asc' ? -1 : 1;
        } else {
            return dir === 'asc' ? 1 : -1;
        }
    }

    private compareTristates(tristate1: 'TRUE' | 'FALSE' | 'UNANSWERED', tristate2: 'TRUE' | 'FALSE' | 'UNANSWERED', dir: SortDirection) {
        if (tristate1 === tristate2) {
            return 0;
        } else if (tristate1 === 'TRUE') {
            return dir === 'asc' ? -1 : 1;
        } else if (tristate1 === 'FALSE') {
            if (tristate2 === 'TRUE') {
                return dir === 'asc' ? 1 : -1;
            } else if (tristate2 === 'UNANSWERED') {
                return dir === 'asc' ? -1 : 1;
            }
        } else if (tristate1 === 'UNANSWERED') {
            return dir === 'asc' ? 1 : -1;
        }
    }

}
